import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Search, UserCheck, UserPlus, UserX } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { EmptyState, ErrorState } from '@/components/shared/states';
import { PageHeader } from '@/components/shared/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { hasAllPermissions, hasPermission, PERMISSIONS } from '@/lib/permissions';
import {
  JoinRequestReviewDialog,
  type MemberJoinRequestRow,
} from '@/pages/people/join-requests-page';
import { PeopleSubnav } from '@/pages/people/people-subnav';
import { useChurchStore } from '@/stores/church-store';
import { formatDateTime, formatNumber } from '@/utils/format';

interface JoinRequestDuplicateScanResult {
  summary: {
    total: number;
    withMatches: number;
    withoutMatches: number;
  };
  requests: MemberJoinRequestRow[];
}

type BulkAction = 'merge' | 'approve' | 'reject' | null;

const displayName = (row: Pick<MemberJoinRequestRow, 'firstName' | 'lastName'>) =>
  [row.firstName, row.lastName].filter(Boolean).join(' ');

export const JoinRequestDuplicatesPage = () => {
  const { t } = useTranslation('people');
  const { t: tErrors } = useTranslation('errors');
  const { t: tCommon } = useTranslation();
  const queryClient = useQueryClient();
  const membership = useChurchStore((state) => state.membership);
  const [shouldRun, setShouldRun] = useState(false);
  const [selected, setSelected] = useState<MemberJoinRequestRow | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkAction, setBulkAction] = useState<BulkAction>(null);

  const canDecide = hasPermission(membership, PERMISSIONS.PEOPLE_CREATE);
  const canMerge = hasAllPermissions(membership, [
    PERMISSIONS.PEOPLE_CREATE,
    PERMISSIONS.PEOPLE_EDIT,
  ]);

  const scan = useQuery({
    queryKey: ['people', 'join-requests', 'duplicate-check'],
    queryFn: () => api.post<JoinRequestDuplicateScanResult>('/people/join-requests/duplicate-check'),
    enabled: shouldRun,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['people', 'join-requests'] });
    void scan.refetch();
  };

  const checkOne = useMutation({
    mutationFn: (requestId: string) =>
      api.post<MemberJoinRequestRow>(`/people/join-requests/${requestId}/duplicate-check`),
    onSuccess: (request) => {
      setSelected(request);
      void refresh();
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? tErrors(error.code, error.params) : tErrors('UNEXPECTED'));
    },
  });

  const bulkMerge = useMutation({
    mutationFn: (requestIds?: string[]) =>
      api.post<{ merged: string[]; skipped: string[]; failed: Array<{ requestId: string; code: string }> }>(
        '/people/join-requests/bulk/merge-matches',
        { requestIds },
      ),
    onSuccess: (result) => {
      toast.success(t('joinRequests.duplicates.bulkMerged', { count: result.merged.length }));
      setBulkAction(null);
      setSelectedIds([]);
      refresh();
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? tErrors(error.code, error.params) : tErrors('UNEXPECTED'));
    },
  });

  const bulkApprove = useMutation({
    mutationFn: (requestIds?: string[]) =>
      api.post<{ approved: string[]; skipped: string[]; failed: Array<{ requestId: string; code: string }> }>(
        '/people/join-requests/bulk/approve-new',
        { requestIds },
      ),
    onSuccess: (result) => {
      toast.success(t('joinRequests.duplicates.bulkApproved', { count: result.approved.length }));
      setBulkAction(null);
      setSelectedIds([]);
      refresh();
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? tErrors(error.code, error.params) : tErrors('UNEXPECTED'));
    },
  });

  const bulkReject = useMutation({
    mutationFn: (requestIds: string[]) =>
      api.post<{ rejected: string[]; failed: Array<{ requestId: string; code: string }> }>(
        '/people/join-requests/bulk/reject',
        { requestIds },
      ),
    onSuccess: (result) => {
      toast.success(t('joinRequests.duplicates.bulkRejected', { count: result.rejected.length }));
      setBulkAction(null);
      setSelectedIds([]);
      refresh();
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? tErrors(error.code, error.params) : tErrors('UNEXPECTED'));
    },
  });

  const rows = scan.data?.requests ?? [];
  const busy = bulkMerge.isPending || bulkApprove.isPending || bulkReject.isPending;

  const summary = useMemo(() => {
    if (!scan.data) return null;
    return [
      { label: t('joinRequests.duplicates.summary.total'), value: formatNumber(scan.data.summary.total) },
      { label: t('joinRequests.duplicates.summary.withMatches'), value: formatNumber(scan.data.summary.withMatches) },
      {
        label: t('joinRequests.duplicates.summary.withoutMatches'),
        value: formatNumber(scan.data.summary.withoutMatches),
      },
    ];
  }, [scan.data, t]);

  const toggleSelected = (requestId: string) => {
    setSelectedIds((current) =>
      current.includes(requestId) ? current.filter((id) => id !== requestId) : [...current, requestId],
    );
  };

  const columns: DataTableColumn<MemberJoinRequestRow>[] = [
    {
      id: 'select',
      header: '',
      cell: (row) => (
        <input
          type="checkbox"
          checked={selectedIds.includes(row.id)}
          onChange={() => toggleSelected(row.id)}
          aria-label={t('joinRequests.duplicates.selectRequest', { name: displayName(row) })}
        />
      ),
    },
    {
      id: 'submittedAt',
      header: t('joinRequests.columns.submittedAt'),
      cell: (row) => formatDateTime(row.createdAt),
    },
    {
      id: 'name',
      header: t('joinRequests.columns.name'),
      cell: (row) => displayName(row),
    },
    {
      id: 'contact',
      header: t('joinRequests.columns.contact'),
      cell: (row) => [row.email, row.phone].filter(Boolean).join(' · ') || '—',
    },
    {
      id: 'matches',
      header: t('joinRequests.columns.matches'),
      cell: (row) =>
        row.suggestedMatches.length > 0 ? (
          <Badge variant="warning">
            {t('joinRequests.columns.matchCount', { count: row.suggestedMatches.length })}
          </Badge>
        ) : (
          '—'
        ),
    },
    {
      id: 'actions',
      header: '',
      cell: (row) => (
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            loading={checkOne.isPending && selected?.id === row.id}
            onClick={() => checkOne.mutate(row.id)}
          >
            {t('joinRequests.duplicates.checkOne')}
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => setSelected(row)}>
            {t('joinRequests.duplicates.review')}
          </Button>
        </div>
      ),
    },
  ];

  const selectedWithMatches = rows.filter(
    (row) => selectedIds.includes(row.id) && row.suggestedMatches.some((match) => !match.linked),
  );
  const selectedWithoutMatches = rows.filter(
    (row) => selectedIds.includes(row.id) && row.suggestedMatches.length === 0,
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('joinRequests.duplicates.title')}
        description={t('joinRequests.duplicates.description')}
        breadcrumbs={[
          { label: t('joinRequests.title'), to: '/people/join-requests' },
          { label: t('joinRequests.duplicates.title') },
        ]}
      />
      <PeopleSubnav />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('joinRequests.duplicates.runTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center justify-between gap-4">
          <p className="text-body-sm text-fg-secondary">{t('joinRequests.duplicates.runHint')}</p>
          <Button type="button" onClick={() => setShouldRun(true)} loading={scan.isFetching} disabled={scan.isFetching}>
            {scan.isFetching ? <Loader2 className="mr-2 size-4 animate-spin" /> : <Search className="mr-2 size-4" />}
            {t('joinRequests.duplicates.runCheck')}
          </Button>
        </CardContent>
      </Card>

      {scan.isError ? (
        <ErrorState
          title={t('joinRequests.duplicates.errorTitle')}
          body={t('joinRequests.duplicates.errorDescription')}
          onRetry={() => void scan.refetch()}
        />
      ) : null}

      {shouldRun && scan.data && summary ? (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            {summary.map((item) => (
              <Card key={item.label}>
                <CardContent className="pt-6">
                  <p className="text-caption text-fg-secondary">{item.label}</p>
                  <p className="text-heading-sm font-semibold tabular-nums">{item.value}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {canDecide ? (
            <div className="flex flex-wrap justify-end gap-2">
              {canMerge ? (
                <Button
                  type="button"
                  variant="outline"
                  disabled={busy || rows.filter((row) => row.suggestedMatches.some((match) => !match.linked)).length === 0}
                  onClick={() => setBulkAction('merge')}
                >
                  <UserCheck className="mr-2 size-4" />
                  {t('joinRequests.duplicates.connectAllMatches')}
                </Button>
              ) : null}
              <Button
                type="button"
                variant="outline"
                disabled={busy || scan.data.summary.withoutMatches === 0}
                onClick={() => setBulkAction('approve')}
              >
                <UserPlus className="mr-2 size-4" />
                {t('joinRequests.duplicates.approveAllNew')}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={busy || selectedIds.length === 0}
                onClick={() => setBulkAction('reject')}
              >
                <UserX className="mr-2 size-4" />
                {t('joinRequests.duplicates.rejectSelected', { count: selectedIds.length })}
              </Button>
            </div>
          ) : null}

          {rows.length === 0 ? (
            <EmptyState title={t('joinRequests.empty')} body={t('joinRequests.emptyDescription')} />
          ) : (
            <DataTable rows={rows} columns={columns} rowKey={(row) => row.id} loading={scan.isLoading} />
          )}

          {selectedIds.length > 0 && canDecide ? (
            <Card>
              <CardContent className="flex flex-wrap items-center justify-between gap-3 pt-6">
                <p className="text-body-sm text-fg-secondary">
                  {t('joinRequests.duplicates.selectedSummary', {
                    total: selectedIds.length,
                    withMatches: selectedWithMatches.length,
                    withoutMatches: selectedWithoutMatches.length,
                  })}
                </p>
                <div className="flex flex-wrap justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={busy || selectedWithMatches.length === 0}
                    onClick={() => bulkMerge.mutate(selectedWithMatches.map((row) => row.id))}
                  >
                    {t('joinRequests.duplicates.connectSelected', { count: selectedWithMatches.length })}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={busy || selectedWithoutMatches.length === 0}
                    onClick={() => bulkApprove.mutate(selectedWithoutMatches.map((row) => row.id))}
                  >
                    {t('joinRequests.duplicates.approveSelected', { count: selectedWithoutMatches.length })}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : null}
        </>
      ) : null}

      {!shouldRun ? (
        <EmptyState
          icon={<Search className="size-8" aria-hidden />}
          title={t('joinRequests.duplicates.notRunTitle')}
          body={t('joinRequests.duplicates.notRunDescription')}
        />
      ) : null}

      <JoinRequestReviewDialog
        request={selected}
        open={selected !== null}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
      />

      <ConfirmDialog
        open={bulkAction === 'merge'}
        onOpenChange={(open) => {
          if (!open) setBulkAction(null);
        }}
        title={t('joinRequests.duplicates.mergeConfirmTitle')}
        description={t('joinRequests.duplicates.mergeConfirmDescription')}
        confirmLabel={t('joinRequests.duplicates.connectAllMatches')}
        cancelLabel={tCommon('action.cancel')}
        onConfirm={() => bulkMerge.mutate(selectedIds.length > 0 ? selectedWithMatches.map((row) => row.id) : undefined)}
        loading={bulkMerge.isPending}
      />

      <ConfirmDialog
        open={bulkAction === 'approve'}
        onOpenChange={(open) => {
          if (!open) setBulkAction(null);
        }}
        title={t('joinRequests.duplicates.approveConfirmTitle')}
        description={t('joinRequests.duplicates.approveConfirmDescription')}
        confirmLabel={t('joinRequests.duplicates.approveAllNew')}
        cancelLabel={tCommon('action.cancel')}
        onConfirm={() =>
          bulkApprove.mutate(selectedIds.length > 0 ? selectedWithoutMatches.map((row) => row.id) : undefined)
        }
        loading={bulkApprove.isPending}
      />

      <ConfirmDialog
        open={bulkAction === 'reject'}
        onOpenChange={(open) => {
          if (!open) setBulkAction(null);
        }}
        title={t('joinRequests.duplicates.rejectConfirmTitle')}
        description={t('joinRequests.duplicates.rejectConfirmDescription', { count: selectedIds.length })}
        confirmLabel={t('joinRequests.duplicates.rejectSelectedAction')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        onConfirm={() => bulkReject.mutate(selectedIds)}
        loading={bulkReject.isPending}
      />
    </div>
  );
};
